import { useContext, useEffect, useState, type ReactNode } from "react";
import { EmpresaAtivaCtx as Ctx } from "./empresaAtivaContext";
import {
  aplicarMarca,
  gravarEmpresaDaPele,
  peleAtual,
  peleDesejada,
  recarregarSemPeleForcada,
} from "../lib/skin";
import { marcaDoTenant } from "../lib/tenant";
import { apiFetch, ApiError, EMPRESA_STORAGE_KEY, setEmpresaAtivaId } from "../lib/api";
import { empresaInicial, lembrarEmpresa, podeRecarregarPelaPele } from "../lib/empresaDaAba";
import { classificarErroEmpresa, comTentativas, type EmpresaErro } from "../lib/erroSessao";
import { gravarUltimaMarca, marcaDaEmpresa } from "../lib/marcaLogin";
import { useAuth } from "./useAuth";

export interface Empresa {
  id: string;
  nome: string;
  slug: string;
  /**
   * CNPJ e remetente da empresa. Vêm de `/me/empresas` porque a PRÉVIA da
   * proposta precisa deles pra ser fiel: os dois saem no rodapé do documento que
   * o cliente recebe. Antes a prévia cravava `null` e mentia (29/07).
   */
  cnpj?: string | null;
  email_remetente?: string | null;
  cor_destaque?: string | null;
  cor_acento?: string | null;
  logo_url?: string | null;
  /**
   * A MARCA que a empresa veste (202609051551): m5 (âmbar/Montserrat da M5), regula (navy da
   * Regula360) ou vortek (porta neutra, white-label — Átomo e os próximos). Antes era derivada
   * do slug em 7 lugares; agora é dado. Quem ainda não recebeu o campo cai no slug (fallback).
   */
  marca?: "m5" | "regula" | "vortek" | null;
  /** O domínio público DA empresa (empresas.url_publica). null: ainda não gravado (decisão do dono na Regula). */
  url_publica?: string | null;
  ativo: boolean;
  role_empresa: "admin" | "sócio" | "colaborador";
  // RBAC (142): perfil do usuário nesta empresa + matriz perfil→áreas da empresa.
  perfil?: "admin" | "financeiro" | "operacao" | "comercial" | "socio" | "cliente";
  perfis_acesso?: Record<string, string[]> | null;
  // Módulos contratados (control plane, NÍVEL 1). Convenção: ausência/true =
  // ligado; só esconde quando explicitamente false. O que a empresa contratou.
  modulos?: Record<string, boolean> | null;
  // RBAC modelo direto (NÍVEL 3): papel DESTA pessoa nesta empresa. null = herda
  // users.role global. "encarregado" = "Equipe" na UI.
  meu_papel?: "admin" | "encarregado" | "cliente" | null;
  // RBAC modelo direto (NÍVEL 2): grant usuário×módulo/item DESTA pessoa nesta
  // empresa (empresa_users.modulos). null = herda tudo que a empresa contratou.
  meu_grant?: Record<string, true | string[]> | null;
  // 145: como chamar o cliente final no Portal (Cliente/Contratante…).
  portal_label?: string | null;
  // 144: dono do cliente (pode gerenciar a própria equipe via /equipe).
  dono_user_id?: string | null;
  site_config?: {
    site_url?: string;
    instagram?: string;
    facebook?: string;
    linkedin?: string;
    whatsapp?: string;
    email_url?: string;
  } | null;
}

/**
 * POR QUE existe (incidente 24/07): o `.catch(() => setEmpresas([]))` engolia
 * TUDO. Token rejeitado (relógio do laptop 2 dias atrasado → 401), usuário sem
 * linha em `empresa_users`, e API fora do ar davam na MESMA tela muda: preso em
 * "Carregando…" com o menu curto. Sem saber qual dos três, a pessoa não tem o
 * que fazer — e quem for ajudar começa do zero. Agora cada causa tem nome.
 *
 * A classificação (e a cópia de cada caso) vive em `lib/erroSessao` — pura, e
 * segurada por teste desde o 401 intermitente de 28/07.
 */
export type { EmpresaErro };

// O tipo e o `createContext` moram em `./empresaAtivaContext` (ver o porquê lá).
export type { EmpresaCtx } from "./empresaAtivaContext";

/** Prazo da chamada que decide se a pessoa entra. Ver o uso, abaixo. */
const PRAZO_EMPRESAS_MS = 8_000;

export function EmpresaProvider({ children }: { children: ReactNode }) {
  const { getAccessToken, user, session } = useAuth();
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  // A EMPRESA DESTA ABA — não a do navegador. `empresaInicial()` prefere o
  // carimbo da aba (sessionStorage) e só cai no localStorage quando esta aba
  // ainda não escolheu nada (aba nova abre na última empresa usada, como antes).
  //
  // 🔴 Antes isto era `localStorage.getItem(...)` direto, e era o furo que
  // desmentia todo o resto do arquivo: o ouvinte de `storage` lá embaixo se
  // recusa, de propósito, a trocar a empresa desta aba — mas bastava UMA recarga
  // (F5, service worker novo, ou o reload que este mesmo hook fabricava logo
  // abaixo) pra ela adotar em silêncio a empresa de outra aba. Provado ao vivo
  // em 28/07: aba em "M5 Group · Painel" voltou "Vitrine — Obras · Painel" na
  // carga seguinte. Ver `lib/empresaDaAba.ts`.
  const [ativaId, setAtivaId] = useState<string | null>(() => empresaInicial());
  // 🔴 `loading` NÃO É MAIS UM INTERRUPTOR — é DERIVADO de "a lista DESTE usuário já
  // voltou?" (15/09/2026, visto ao vivo na A Pagar).
  //
  // Era um `useState` que o efeito abaixo ligava a cada `access_token` novo. O token
  // renova sozinho (~1×/hora, ou no clique perto de vencer — `lib/sessaoViva`), o
  // efeito re-rodava, `loading` voltava a true e o `PrivateRoute` trocava a árvore
  // INTEIRA por "Carregando…": gaveta fechada, filtro zerado, formulário perdido.
  // Crachá novo não muda vínculo com empresa nenhuma — não havia o que recarregar.
  //
  // E o interruptor tinha um segundo furo, no boot: no render em que `user` chegava,
  // `loading` ainda era o false do "sem usuário", a página MONTAVA por um instante e
  // só depois o efeito a derrubava. Derivado, esse render já nasce carregando.
  const [resolvidoPara, setResolvidoPara] = useState<string | null>(null);
  const [erro, setErro] = useState<EmpresaErro | null>(null);
  const token = session?.access_token ?? getAccessToken();
  // Sem token não há o que esperar (#953): a tela destrava e o efeito tenta quando ele chegar.
  const loading = !!user && !!token && resolvidoPara !== user.id;
  // Outra aba trocou de empresa enquanto esta estava aberta. Guardamos o NOME
  // pra avisar; esta aba NÃO muda de empresa por causa disso (ver abaixo).
  const [trocaEmOutraAba, setTrocaEmOutraAba] = useState<string | null>(null);

  // SÍNCRONO, no corpo do provider, de propósito: o header precisa valer ANTES
  // do primeiro fetch dos filhos. Num useEffect não valeria — efeito de pai roda
  // DEPOIS do de filho, e a primeira query já teria saído com a empresa do
  // localStorage (que pode ser a que OUTRA aba acabou de gravar).
  setEmpresaAtivaId(ativaId);

  useEffect(() => {
    if (!user) {
      setEmpresas([]);
      setErro(null);
      setResolvidoPara(null);
      return;
    }
    // ANTES: `if (!token) return;` deixava loading=true PRA SEMPRE quando o user
    // já existia mas o token ainda não tinha populado naquele instante (o efeito
    // só re-roda por user.id, que não muda) → sidebar montada + conteúdo preso em
    // "Carregando…" + seletor de empresas sumido. Agora: sem token, `loading` é
    // false por derivação e o efeito re-roda sozinho quando o token chega.
    if (!token) return;
    // Token RENOVADO com a lista deste usuário já na mão: nada a buscar. Buscar de
    // novo era o que desmontava a tela (ver `resolvidoPara`). Se a busca anterior
    // FALHOU, o token novo tenta outra vez — é a saída de um 401 por crachá vencido.
    if (resolvidoPara === user.id && erro === null) return;

    const userId = user.id;
    setResolvidoPara(null);
    setErro(null);
    // ESTA é a chamada que decide se a pessoa entra ou vê tela de erro — e ela
    // roda UMA vez por boot, fora do React Query (sem a rede de repetição dele).
    // Um soluço de 300ms do Supabase virava "Sua sessão expirou" na cara do
    // dono (28/07 00:42). Agora insiste 2× no que passa sozinho (5xx/rede) e
    // nunca no que não passa (401/403).
    comTentativas(
      // PRAZO (28/07): sem ele, uma requisição pendurada não erra e não volta —
      // `loading` ficava true, o `.finally` nunca rodava, e o PrivateRoute
      // renderizava "Carregando…" pra sempre. Estourando vira 408, que
      // `deveTentarDeNovo` já repete; esgotadas as tentativas, cai no `.catch`
      // abaixo e a pessoa vê "Instabilidade nossa" com botão — não um spinner.
      () => apiFetch<{ empresas: Empresa[] }>("/me/empresas", token, { prazoMs: PRAZO_EMPRESAS_MS }),
      (e) => (e instanceof ApiError ? e.status : 0)
    )
      .then((res) => {
        const list = res.empresas ?? [];
        setEmpresas(list);
        // Lista vazia = a pessoa entrou, mas não tem empresa ATIVA vinculada
        // (/me/empresas já filtra por empresas.ativo). Não adianta esperar.
        setErro(list.length === 0 ? "sem-empresa" : null);
        // Garante que ativaId é uma empresa válida; senão fixa a primeira
        if (list.length > 0) {
          const valid = ativaId ? list.find((e) => e.id === ativaId) : undefined;
          const escolhida = valid ?? list[0];
          if (!valid) {
            setAtivaId(escolhida.id);
            // Porta única: carimba a ABA + a memória do próximo boot + o slug da
            // pele. O slug junto é o que impede o reload de correção lá embaixo
            // de disparar na carga seguinte.
            lembrarEmpresa(escolhida.id, escolhida.slug);
          }
          // Login bem-sucedido → lembra a marca do PRODUTO da empresa ativa
          // ('ultima_marca'): o próximo /login já veste a marca certa. Dono da
          // plataforma fica na porta neutra VORTEK (opera vários clientes).
          gravarUltimaMarca(user?.is_owner ? "vortek" : marcaDaEmpresa(escolhida));
        }
      })
      .catch((e: unknown) => {
        setEmpresas([]);
        const st = e instanceof ApiError ? e.status : 0;
        const cod = e instanceof ApiError ? (e.data as { error?: string } | undefined)?.error : undefined;
        setErro(classificarErroEmpresa(st, cod));
      })
      .finally(() => setResolvidoPara(userId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, session?.access_token]);

  function trocar(empresaId: string) {
    const alvo = empresas.find((e) => e.id === empresaId);
    if (!alvo) return;
    setAtivaId(empresaId);
    // AGORA, síncrono, antes do reload — e pela porta única, que grava as três
    // coisas juntas (aba + próximo boot + slug da pele). Se o slug dependesse do
    // efeito lá embaixo, o reload partiria com o da empresa ANTERIOR e a nova
    // nasceria com a pele errada — e se corrigiria com OUTRO reload.
    lembrarEmpresa(empresaId, alvo.slug);
    // Recarrega a aplicação pra invalidar todos os caches de React Query etc.
    // Mais simples e seguro que invalidar query por query.
    //
    // SEM o `?skin=` da empresa ANTERIOR: um `reload()` cru repete a URL
    // inteira, e o parâmetro vence a memória por empresa — foi assim que a
    // Vitrine com `?skin=v2` fez o Átomo abrir na v2 (auditoria 28/07).
    recarregarSemPeleForcada();
  }

  const ativa = empresas.find((e) => e.id === ativaId) ?? null;

  // Carimba a marca no <html> pra a pele v2 escolher o acento (âmbar M5 ×
  // navy Regula). Só aparência, e só sob data-skin="v2" — na v1 o seletor não
  // casa e nada muda. Roda a cada troca de empresa.
  useEffect(() => {
    aplicarMarca(marcaDoTenant(ativa));
    if (!ativa) return;

    // A PELE depende da empresa (v2 = só M5), mas o applySkin() roda no boot —
    // antes de sabermos qual empresa é. Aqui a empresa finalmente chegou:
    // espelha o slug pro próximo boot acertar de primeira.
    gravarEmpresaDaPele(ativa.slug);

    // Se o boot decidiu com informação velha, a pele no ar está errada. Não dá
    // pra corrigir no lugar: a escolha entre página v1 e v2 é feita no RENDER
    // (App.tsx), sem estado reativo. Então recarrega — mas NO MÁXIMO UMA VEZ.
    //
    // 🔴 O "sem risco de laço" do comentário antigo dependia de uma premissa que
    // não é garantida: que gravar o slug conserta a divergência. Se o
    // localStorage estiver bloqueado (aba anônima, storage cheio, política do
    // navegador), `gravarEmpresaDaPele` engole o erro, a leitura seguinte devolve
    // o mesmo valor velho, e este `if` dispara OUTRO reload — pra sempre. Um
    // painel recarregando em laço é indistinguível de um painel travado: a tela
    // fica em "Carregando…", o console limpa a cada volta, as requisições
    // aparecem PENDENTES (a navegação seguinte as mata) e nem dá pra rodar nada
    // na página. Foi essa a cara do incidente de 28/07.
    //
    // O carimbo mora no sessionStorage (por aba, e some quando a aba fecha). Se
    // ele não puder ser gravado, não recarrega — a pele errada é um incômodo
    // visual; o laço é o painel inteiro perdido.
    //
    // 🔴 ESTE RELOAD ERA CRU — e repetia a URL inteira, com o `?skin=` dentro.
    //
    // Era o IRMÃO do reload que a auditoria de 28/07 já tinha endurecido 34
    // linhas acima (`trocar()` → `recarregarSemPeleForcada()`). O comentário que
    // explica o perigo está entre os dois, e mesmo assim este ficou pra trás: o
    // guardrail que proibia `window.location.reload()` fatiava só a `trocar()`.
    //
    // COMO ELE MORDE (reproduzido sem querer em produção, 17/08, na vistoria da
    // Regula — a memória de pele da M5 virou `v1` no meio da sessão):
    //   1. o operador está na Regula e abre `/fiscal?skin=v1` pra comparar peles
    //   2. troca pra M5 no seletor → `lembrarEmpresa` grava o slug `m5group`
    //   3. este efeito vê pele desejada ≠ pele no ar e recarrega — COM o
    //      `?skin=v1` ainda na barra de endereço
    //   4. no boot, `applySkin()` lê o parâmetro e grava `mrg-skin:m5group=v1`
    //
    // A M5 passa a abrir na v1 por causa de uma URL digitada em OUTRA empresa.
    // É o mesmo vazamento do #1457, só que pela porta de trás — e o `?skin=` não
    // precisa nem ser da empresa anterior: basta estar na URL quando o slug já
    // trocou. Largar o parâmetro antes de recarregar fecha os dois casos.
    if (peleDesejada() !== peleAtual() && podeRecarregarPelaPele()) recarregarSemPeleForcada();
  }, [ativa]);

  // OUTRA ABA TROCOU DE EMPRESA.
  //
  // O evento `storage` só dispara em abas que NÃO fizeram a escrita — é
  // exatamente a aba que ficou pra trás, a que o dono viu "virar Vitrine
  // sozinha". Esta aba NÃO se muda por causa disso: ela continua na empresa em
  // que foi aberta (o header já é o do holder, não o do localStorage), porque
  // recarregar aqui jogaria fora o formulário que o dono tem aberto na tela —
  // trocaríamos um dado errado por um trabalho perdido.
  //
  // O que fazemos é AVISAR: sem isso, duas abas em empresas diferentes é uma
  // armadilha silenciosa (foi assim que o ORC-2026-0033 tomou um 404).
  useEffect(() => {
    function aoMudarArmazenamento(e: StorageEvent) {
      if (e.key !== EMPRESA_STORAGE_KEY || !e.newValue) return;
      if (!ativaId || e.newValue === ativaId) {
        setTrocaEmOutraAba(null);
        return;
      }
      const outra = empresas.find((x) => x.id === e.newValue);
      setTrocaEmOutraAba(outra?.nome ?? "outra empresa");
    }
    window.addEventListener("storage", aoMudarArmazenamento);
    return () => window.removeEventListener("storage", aoMudarArmazenamento);
  }, [ativaId, empresas]);

  return (
    <Ctx.Provider value={{ empresas, ativa, trocar, loading, erro, trocaEmOutraAba }}>
      {children}
    </Ctx.Provider>
  );
}

export function useEmpresaAtiva() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useEmpresaAtiva must be inside EmpresaProvider");
  return ctx;
}

/**
 * A MESMA empresa, sem derrubar quem só QUER saber.
 *
 * 🔴 O `throw` acima é certo pra tela que depende da empresa: falhar alto é
 * melhor que renderizar dados de ninguém. Mas ele é errado pra quem usa o nome
 * como ENFEITE — um rótulo de formulário derrubando o editor inteiro de proposta
 * porque a árvore foi montada sem o provider (um teste, uma prévia, um print) é
 * troca ruim. Quem chama aqui está dizendo, explicitamente, que sabe seguir sem.
 */
export function useEmpresaAtivaOpcional() {
  return useContext(Ctx) ?? null;
}
